// The team row inside the profile dropdown: current team, its spendable
// credits, and a flyout to switch teams or start creating a new one.
//
// A flyout rather than a nested dropdown, and inside the profile menu rather
// than beside it, for two reasons:
//
//   * The standalone TeamSwitcher pill hid itself whenever you belonged to
//     exactly one team — which is every account until someone is invited
//     somewhere — so the common case showed no team at all.
//   * Two sibling dropdowns competing for the same corner is a worse target
//     than one menu with a submenu, and the profile menu is already where
//     "who am I" lives.
//
// Renders nothing when the flag is off, so the menu is byte-identical to
// pre-teams.

import { useState } from 'react'
import { Check, ChevronRight, Plus, Users, User as UserIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { useActiveTeam } from '../../contexts/ActiveTeamContext'
import { useTeamsEnabled } from '../../features/teams/useTeamsEnabled'
import { useTeamCredits } from '../../hooks/useTeams'
import { cn } from '../../lib/utils'
import { useSettingsModal } from '../settings/settings-modal/SettingsModalProvider'
import { trackInteraction } from '../../analytics/interactionAnalytics'
import { countBucket } from '../../analytics/buckets'

// There is no `teamLabel` helper any more. It substituted a fixed "Personal"
// string for a personal team's stored name; personal teams are renameable now,
// so that would silently discard whatever the Owner typed. The UserIcon beside
// the name is what still marks which team is the personal one.

export default function UserMenuTeamSection({ closeMenu }: { closeMenu: () => void }) {
  const { t } = useTranslation('header')
  const enabled = useTeamsEnabled()
  const { teams, activeTeam, setActiveTeamId } = useActiveTeam()
  const { open: openSettingsModal } = useSettingsModal()
  const [flyoutOpen, setFlyoutOpen] = useState(false)

  const { data: credits, isPending } = useTeamCredits(activeTeam?.id ?? null, true)

  if (!enabled || !activeTeam) return null

  const current = activeTeam.name

  return (
    <div className="relative border-b border-white/10 pb-1 mb-1">
      <button
        type="button"
        onClick={() => setFlyoutOpen((v) => !v)}
        onMouseEnter={() => setFlyoutOpen(true)}
        data-testid="user-menu-team-row"
        aria-haspopup="menu"
        aria-expanded={flyoutOpen}
        className="flex w-full items-center gap-3 px-4 py-2.5 text-left transition-colors hover:bg-white/10 cursor-pointer"
      >
        {activeTeam.kind === 'personal' ? (
          <UserIcon className="h-4 w-4 shrink-0 text-white/70" aria-hidden="true" />
        ) : (
          <Users className="h-4 w-4 shrink-0 text-white/70" aria-hidden="true" />
        )}
        <span className="min-w-0 flex-1">
          <span
            className="block truncate text-sm text-white/90"
            data-testid="user-menu-active-team-name"
            title={current}
          >
            {current}
          </span>
          {/* Credits only, never a currency amount. Resolves the same way the
              charge does, so the number shown is the number spendable.
              NEVER render 0 while loading: someone who has paid for credits
              seeing "0 credits" flash before the real figure arrives has every
              reason to think their balance is gone. A skeleton says "not yet",
              a zero says "none". */}
          <span className="block text-xs font-medium text-white/50" data-testid="user-menu-team-credits">
            {isPending ? (
              <span
                aria-label={t('teamSection.creditsLoading')}
                data-testid="user-menu-team-credits-loading"
                className="inline-block h-3 w-16 animate-pulse rounded bg-white/15 align-middle"
              />
            ) : (
              t('teamSection.credits', { count: credits?.balance ?? 0 })
            )}
          </span>
        </span>
        <ChevronRight
          aria-hidden="true"
          className={cn(
            'h-4 w-4 shrink-0 text-white/50 transition-transform duration-150',
            flyoutOpen && 'rotate-90',
          )}
        />
      </button>

      {flyoutOpen && (
        <div
          role="menu"
          data-testid="user-menu-team-flyout"
          onMouseLeave={() => setFlyoutOpen(false)}
          // Opens to the LEFT. The profile menu is right-aligned in the header,
          // so a right-hand flyout would run off-viewport on narrower windows.
          className={cn(
            'absolute right-full top-0 mr-1 z-50 max-h-72 w-56 overflow-y-auto',
            'rounded-lg border border-white/10 bg-[#1c1c1e] py-1 shadow-xl',
          )}
        >
          {teams.map((team) => {
            const selected = team.id === activeTeam.id
            return (
              <button
                key={team.id}
                type="button"
                role="menuitemradio"
                aria-checked={selected}
                data-testid={`user-menu-team-option-${team.id}`}
                onClick={() => {
                  if (team.id !== activeTeam.id) {
                    trackInteraction('teams_active_team_switched', {
                      team_id: team.id,
                      entry_point: 'user_menu',
                      owner_type: team.kind === 'personal' ? 'personal' : 'team',
                      team_count_bucket: countBucket(teams.length),
                      actor_role: team.myRole,
                    })
                  }
                  setActiveTeamId(team.id)
                  setFlyoutOpen(false)
                }}
                className="flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm text-white/80 transition-colors hover:bg-white/10 hover:text-white cursor-pointer"
              >
                {team.kind === 'personal' ? (
                  <UserIcon className="h-4 w-4 shrink-0 text-white/60" aria-hidden="true" />
                ) : (
                  <Users className="h-4 w-4 shrink-0 text-white/60" aria-hidden="true" />
                )}
                <span className="min-w-0 flex-1 truncate">{team.name}</span>
                {selected && <Check className="h-4 w-4 shrink-0 text-white/70" aria-hidden="true" />}
              </button>
            )
          })}

          {/* Creating a team happens in the settings dialog, not here: this
              flyout closes on mouse-leave, which would throw away a half-typed
              name, and the dialog already owns Escape and the scroll lock. */}
          <div className="my-1 border-t border-white/10" role="none" />
          <button
            type="button"
            role="menuitem"
            data-testid="user-menu-team-create"
            onClick={() => {
              setFlyoutOpen(false)
              closeMenu()
              openSettingsModal('teams', { teamsView: 'create' })
            }}
            className="flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm text-white/80 transition-colors hover:bg-white/10 hover:text-white cursor-pointer"
          >
            <Plus className="h-4 w-4 shrink-0 text-white/60" aria-hidden="true" />
            <span className="min-w-0 flex-1 truncate">{t('teamSection.createTeam')}</span>
          </button>
        </div>
      )}
    </div>
  )
}
